import React, { useEffect, useRef, useState, useMemo } from "react";
import { translate } from "../../../../common/providers";
import {
	SidePanel,
	SidePanelContent,
	LimitedTextField,
	CustomDatePicker,
	CustomButton,
	CustomTextField,
} from "../../../../common/components";
import { ApiService, CoveragePhaseService } from "../../../../api";
import { debounce } from "../../../../common/utils";
import styles from "./PhaseDialog.module.css";

const PhaseDialog = ({ phase, open, onClose, onSubmit, isNew = true, projectId }) => {
	const [name, setName] = useState(phase.name || "");
	const [description, setDescription] = useState(phase.description || "");
	const [startDate, setStartDate] = useState(phase.startDate ? new Date(phase.startDate) : null);
	const [endDate, setEndDate] = useState(phase.endDate ? new Date(phase.endDate) : null);

	const textFieldSx = {
		"& .MuiOutlinedInput-root": {
			borderRadius: "4px",
			height: "40px",
			"& input": {
				padding: "8px 14px",
				height: "auto",
				lineHeight: 1.5,
			},
			"& .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px",
			},
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px",
			},
			"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderWidth: "2px",
			},
		},
	};

	const datePickerSx = {
		"& .MuiOutlinedInput-root": {
			borderRadius: "4px",
			height: "40px",
			"& input": {
				padding: "8px 14px",
				height: "auto",
				lineHeight: 1.5,
			},
			"& .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px",
			},
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderWidth: "1px",
			},
			"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderWidth: "2px",
			},
		},
	};

	const datePickerErrorSx = {
		...datePickerSx,
		"& .MuiOutlinedInput-root": {
			...datePickerSx["& .MuiOutlinedInput-root"],
			"&.Mui-error .MuiOutlinedInput-notchedOutline": {
				borderColor: "var(--red-500) !important",
				borderWidth: "1px",
			},
			"&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: "var(--red-500) !important",
			},
			"&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: "var(--red-500) !important",
				borderWidth: "2px",
			},
		},
	};

	const [isNameChecking, setIsNameChecking] = useState(false);
	const [isNameDuplicate, setIsNameDuplicate] = useState(false);
	const [isDateRangeInvalid, setIsDateRangeInvalid] = useState(false);
	const cancelTokenRef = useRef(null);
	const clearable = true;

	const debouncedCheckName = useMemo(
		() =>
			debounce(async (trimmedName) => {
				if (cancelTokenRef.current) {
					ApiService.cancelTokens(cancelTokenRef.current);
				}
				cancelTokenRef.current = ApiService.getCancelTokenSource();
				CoveragePhaseService.existsByName({ projectId, name: trimmedName }, cancelTokenRef.current.token)
					.then((exists) => {
						setIsNameDuplicate(!!exists.value);
					})
					.catch(() => {
						setIsNameDuplicate(false);
					})
					.finally(() => setIsNameChecking(false));
			}, 400),
		[projectId]
	);
	useEffect(() => {
		if (open) {
			setName(phase.name || "");
			setDescription(phase.description || "");
			setStartDate(phase.startDate ? new Date(phase.startDate) : null);
			setEndDate(phase.endDate ? new Date(phase.endDate) : null);
		}
	}, [open, phase]);

	const validateDateRange = (start, end) => {
		if (!start || !end) {
			return true;
		}
		const startDateObj = new Date(start);
		const endDateObj = new Date(end);
		startDateObj.setHours(0, 0, 0, 0);
		endDateObj.setHours(0, 0, 0, 0);
		return endDateObj >= startDateObj;
	};

	useEffect(() => {
		if (startDate && endDate) {
			const isValid = validateDateRange(startDate, endDate);
			setIsDateRangeInvalid(!isValid);
		} else {
			setIsDateRangeInvalid(false);
		}
	}, [startDate, endDate]);

	const isFormValid =
		name.trim() !== "" &&
		description.trim() !== "" &&
		startDate !== null &&
		!isNameDuplicate &&
		!isNameChecking &&
		!isDateRangeInvalid;

	useEffect(() => {
		const trimmed = name.trim();
		const isOriginalName = !isNew && trimmed === (phase.name || "").trim();
		const shouldSkipCheck = !open || !projectId || isOriginalName || trimmed.length === 0;

		if (shouldSkipCheck) {
			setIsNameDuplicate(false);
			setIsNameChecking(false);
		} else {
			setIsNameChecking(true);
			setIsNameDuplicate(false);
			debouncedCheckName(trimmed);
		}

		return () => {
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
	}, [name, isNew, phase.name, projectId, open, debouncedCheckName]);

	const formatDateForJava = (date) => {
		if (!date) {
			return null;
		}
		try {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}T00:00:00`;
		} catch (error) {
			console.error("Error formatting date for Java:", error);
			return null;
		}
	};

	const resetForm = () => {
		setName("");
		setDescription("");
		setStartDate(null);
		setEndDate(null);
	};

	const handleSubmit = () => {
		onSubmit({
			id: phase.id,
			name,
			description,
			startDate: formatDateForJava(startDate),
			endDate: formatDateForJava(endDate),
		});
		resetForm();
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleDescriptionChange = (e) => {
		const { value } = e.target;
		if (value.length <= 1000) {
			setDescription(value);
		} else {
			setDescription(value.substring(0, 1000));
		}
	};

	return (
		<SidePanel open={open} size={45} onClose={handleClose}>
			<SidePanelContent
				title={
					isNew
						? translate("project-phases.dialog.create.title")
						: translate("project-phases.dialog.edit.title")
				}
				onClose={handleClose}
			>
				<div className={styles.formContainer}>
					<div className={styles.formContent}>
						<div className={`${styles.formField} ${styles.nameField}`}>
							<span className={styles.fieldLabel}>
								{translate("project-phases.field.name")} <span className={styles.required}>*</span>
							</span>
							<LimitedTextField
								fullWidth
								error={isNameDuplicate}
								maxLength={256}
								placeholder={translate("project-phases.phase-dialog.enter-name")}
								showCounter={false}
								sx={textFieldSx}
								value={name}
								onChange={setName}
							/>
							{isNameDuplicate && (
								<div className={styles.errorMessage}>
									{translate("project-phases.validation.name-exists")}
								</div>
							)}
						</div>
						<div className={`${styles.formField} ${styles.descriptionField}`}>
							<span className={styles.fieldLabel}>
								{translate("project-phases.field.description")}{" "}
								<span className={styles.required}>*</span>
							</span>
							<CustomTextField
								fullWidth
								multiline
								placeholder={translate("project-phases.phase-dialog.start-writing-description")}
								rows={5}
								size="small"
								sx={{
									"& .MuiOutlinedInput-root": {
										alignItems: "flex-start",
										minHeight: "140px",
										"& textarea": {
											padding: "16.5px 14px",
											resize: "vertical",
											lineHeight: "1.5",
											minHeight: "calc(1.5em * 1.5)",
											height: "calc(1.5em * 5 + 33px)",
										},
									},
								}}
								value={description}
								variant="outlined"
								onChange={handleDescriptionChange}
							/>
						</div>
						<div className={styles.dateFieldsRow}>
							<div className={styles.dateField}>
								<span className={styles.fieldLabel}>
									{translate("project-phases.field.startDate")}{" "}
									<span className={styles.required}>*</span>
								</span>
								<CustomDatePicker
									clearable={clearable}
									placeholder={translate("project-phases.phase-dialog.select-date")}
									slotProps={{
										textField: {
											placeholder: translate("project-phases.phase-dialog.select-date"),
											fullWidth: true,
											size: "small",
											variant: "outlined",
											sx: datePickerSx,
										},
									}}
									value={startDate}
									onChange={setStartDate}
								/>
							</div>
							<div className={styles.dateField}>
								<span className={styles.fieldLabel}>{translate("project-phases.field.endDate")}</span>
								<CustomDatePicker
									clearable={clearable}
									placeholder={translate("project-phases.phase-dialog.select-date")}
									slotProps={{
										textField: {
											placeholder: translate("project-phases.phase-dialog.select-date"),
											fullWidth: true,
											size: "small",
											variant: "outlined",
											error: isDateRangeInvalid,
											sx: isDateRangeInvalid ? datePickerErrorSx : datePickerSx,
										},
									}}
									value={endDate}
									onChange={setEndDate}
								/>
							</div>
						</div>
					</div>
					<div className={styles.formActions}>
						<CustomButton variant="outlined" onClick={handleClose}>
							{translate("common:btn.cancel")}
						</CustomButton>
						<CustomButton
							color="primary"
							disabled={!isFormValid}
							variant="contained"
							onClick={handleSubmit}
						>
							{translate("common:btn.validate")}
						</CustomButton>
					</div>
				</div>
			</SidePanelContent>
		</SidePanel>
	);
};

export default PhaseDialog;
